import { supabase } from '@/lib/supabase';
import {
  FacturacionCompany,
  FacturacionDocument,
  FacturacionEstablishment,
  FacturacionPointOfSale,
  FacturacionProviderConfig,
  FacturacionUsage,
} from '@/types';

// Invoca la Edge Function `facturacion` y, en caso de error HTTP,
// recupera el cuerpo JSON (donde van los detalles de Lumen).
async function invokeFacturacion<T = any>(
  action: string,
  payload?: Record<string, any>,
  gymId?: string,
): Promise<T> {
  const { data, error } = await supabase.functions.invoke('facturacion', {
    body: { action, payload, ...(gymId ? { gym_id: gymId } : {}) },
  });

  if (error) {
    const ctx = (error as any)?.context;
    if (ctx && typeof ctx.json === 'function') {
      const body = await ctx.json().catch(() => null);
      if (body) return body as T;
    }
    throw error;
  }
  return data as T;
}

const cleanName = (row: any): string | undefined => {
  if (!row?.clients) return undefined;
  const f = (row.clients.first_name || '').trim();
  const l = (row.clients.last_name || '').trim();
  return `${f} ${l}`.trim();
};

const mapDocument = (row: any): FacturacionDocument => ({
  ...row,
  total_amount: row.total_amount != null ? Number(row.total_amount) : null,
  client_name: cleanName(row),
});

export const facturacionApi = {
  // Garantiza una fila de config de proveedor (lazy init, entorno SANDBOX).
  ensureProvider: async (gymId: string, providerId = 'lumen') => {
    const { data: existing } = await supabase
      .from('facturacion_providers')
      .select('id')
      .eq('gym_id', gymId)
      .eq('provider_id', providerId)
      .maybeSingle();
    if (existing) return { success: true, provider_id: providerId };

    const { error } = await supabase
      .from('facturacion_providers')
      .insert({ gym_id: gymId, provider_id: providerId, environment: 'SANDBOX', status: 'pending' });
    if (error) throw error;
    return { success: true, provider_id: providerId };
  },

  getConfig: async (gymId: string, providerId = 'lumen') => {
    const [providerRes, companyRes, estabRes, posRes] = await Promise.all([
      supabase.from('facturacion_providers').select('*').eq('gym_id', gymId).eq('provider_id', providerId).maybeSingle(),
      supabase.from('facturacion_companies').select('*').eq('gym_id', gymId).eq('provider_id', providerId).maybeSingle(),
      supabase.from('facturacion_establishments').select('*').eq('gym_id', gymId).eq('provider_id', providerId).order('created_at'),
      supabase.from('facturacion_points_of_sale').select('*').eq('gym_id', gymId).eq('provider_id', providerId).order('created_at'),
    ]);
    if (providerRes.error) throw providerRes.error;
    if (companyRes.error) throw companyRes.error;
    if (estabRes.error) throw estabRes.error;
    if (posRes.error) throw posRes.error;

    return {
      success: true,
      provider: providerRes.data as FacturacionProviderConfig | null,
      company: companyRes.data as FacturacionCompany | null,
      establishments: (estabRes.data || []) as FacturacionEstablishment[],
      pointsOfSale: (posRes.data || []) as FacturacionPointOfSale[],
    };
  },

  setEnvironment: async (gymId: string, environment: 'SANDBOX' | 'PRODUCTION', providerId = 'lumen') => {
    const { data, error } = await supabase
      .from('facturacion_providers')
      .update({ environment })
      .eq('gym_id', gymId)
      .eq('provider_id', providerId)
      .select()
      .single();
    if (error) throw error;
    return { success: true, provider: data as FacturacionProviderConfig };
  },

  registerCompany: (gymId: string, payload: Record<string, any>) =>
    invokeFacturacion('register_company', payload, gymId),

  registerEstablishment: (gymId: string, payload: Record<string, any>) =>
    invokeFacturacion('register_establishment', payload, gymId),

  registerPointOfSale: (gymId: string, payload: Record<string, any>) =>
    invokeFacturacion('register_point_of_sale', payload, gymId),

  emitDocument: (gymId: string, sourceType: 'sale' | 'membership', sourceId: string) =>
    invokeFacturacion('emit_document', { source_type: sourceType, source_id: sourceId }, gymId),

  downloadPdf: (gymId: string, generationCode: string) =>
    invokeFacturacion<{ success: boolean; url?: string; error?: string }>(
      'download_pdf',
      { generation_code: generationCode },
      gymId,
    ),

  downloadJson: (gymId: string, generationCode: string) =>
    invokeFacturacion<{ success: boolean; url?: string; error?: string }>(
      'download_json',
      { generation_code: generationCode },
      gymId,
    ),

  listDocuments: async (gymId: string) => {
    const { data, error } = await supabase
      .from('facturacion_documents')
      .select('*, clients(first_name, last_name)')
      .eq('gym_id', gymId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return { success: true, documents: (data || []).map(mapDocument) };
  },

  getUsage: async (gymId: string) => {
    const { data, error } = await supabase.rpc('get_facturacion_usage', { p_gym_id: gymId });
    if (error) throw error;
    return { success: true, usage: (data || []) as FacturacionUsage[] };
  },
};
